import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import {
  AUTH_UNAUTHORIZED_EVENT,
  api,
  getApiErrorMessage,
  getApiStatusCode,
} from "@/library/RequestMaker";
import { endpoints } from "@/library/Endpoints";
import {
  clearDashboardSnapshots,
  fetchSharedDashboard,
  getDashboardRequestParams,
  writeDashboardSnapshot,
} from "@/library/dashboardBootstrap";

type AuthMode = "cookie";

type StudentStatus = {
  attendance_status_id: number;
  attendance_status: string;
};

type Student = {
  id: number;
  person_id: number;
  full_name: string;
  nickname: string | null;
  person_status: string;
  group_id: number | null;
  join_date: string | null;
  move_date: string | null;
  leave_date: string | null;
  attendance_status_id: number | null;
  attendance_status: string | null;
  turnstile_status_id: number | null;
  registration_type_id: number | null;
  created_at: string;
  username?: string;
  roles?: string[];
  profile_picture_signed_url?: string | null;
  pending_profile_picture_signed_url?: string | null;
  pending_profile_picture_status?: "pending" | "rejected" | null;
  pending_profile_picture_rejection_reason?: string | null;
};

function areStringArraysEqual(left?: string[], right?: string[]) {
  if (left === right) return true;
  if (!left && !right) return true;
  if (!left || !right) return false;
  if (left.length !== right.length) return false;

  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) return false;
  }

  return true;
}

function areStudentsEqual(left: Student | null, right: Student | null) {
  if (left === right) return true;
  if (!left || !right) return false;

  return (
    left.id === right.id &&
    left.person_id === right.person_id &&
    left.full_name === right.full_name &&
    left.nickname === right.nickname &&
    left.person_status === right.person_status &&
    left.group_id === right.group_id &&
    left.join_date === right.join_date &&
    left.move_date === right.move_date &&
    left.leave_date === right.leave_date &&
    left.attendance_status_id === right.attendance_status_id &&
    left.attendance_status === right.attendance_status &&
    left.turnstile_status_id === right.turnstile_status_id &&
    left.registration_type_id === right.registration_type_id &&
    left.created_at === right.created_at &&
    left.username === right.username &&
    areStringArraysEqual(left.roles, right.roles) &&
    left.profile_picture_signed_url === right.profile_picture_signed_url &&
    left.pending_profile_picture_signed_url ===
      right.pending_profile_picture_signed_url &&
    left.pending_profile_picture_status ===
      right.pending_profile_picture_status &&
    left.pending_profile_picture_rejection_reason ===
      right.pending_profile_picture_rejection_reason
  );
}

function areStudentStatusesEqual(
  left: StudentStatus | null,
  right: StudentStatus | null,
) {
  if (left === right) return true;
  if (!left || !right) return false;

  return (
    left.attendance_status_id === right.attendance_status_id &&
    left.attendance_status === right.attendance_status
  );
}

type SignInResponse = {
  ok: boolean;
  message: string;
  student?: Student;
};

type ValidateResponse = {
  ok: boolean;
  message: string;
  method?: "RC" | "ORM";
  slice?: string;
};

type MeResponse = {
  ok: boolean;
  student?: Student;
};

type DashboardBootstrapResponse = {
  me?: {
    ok?: boolean;
    student?: Student;
  };
};

interface AuthContextValue {
  student: Student | null;
  user: Student | null;
  studentStatus: StudentStatus | null;
  token: string | null;
  authMode: AuthMode;
  isAuthenticated: boolean;
  isLoading: boolean;
  status:
    | "idle"
    | "loading"
    | "success"
    | "autherror"
    | "networkerror"
    | "error";
  error: string;
  signIn: (personId: number, password: string) => Promise<void>;
  hydrateAuthStudent: (student: Student | null) => void;
  refreshUser: () => Promise<void>;
  validateSession: () => Promise<boolean | null>;
  signOut: () => Promise<void>;
  clearError: () => void;
}

const STUDENT_STORAGE_KEY = "student.auth.payload";

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function getStoredStudent(): Student | null {
  const raw = localStorage.getItem(STUDENT_STORAGE_KEY);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as Student;
  } catch {
    localStorage.removeItem(STUDENT_STORAGE_KEY);
    return null;
  }
}

export function AuthProvider({ children }: PropsWithChildren) {
  const [student, setStudent] = useState<Student | null>(() =>
    getStoredStudent(),
  );
  const [studentStatus, setStudentStatus] = useState<StudentStatus | null>(
    () => {
      const storedStudent = getStoredStudent();
      if (
        storedStudent?.attendance_status_id &&
        storedStudent.attendance_status
      ) {
        return {
          attendance_status_id: storedStudent.attendance_status_id,
          attendance_status: storedStudent.attendance_status,
        };
      }
      return null;
    },
  );
  const token: string | null = null;
  const authMode: AuthMode = "cookie";
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState<AuthContextValue["status"]>("idle");

  const clearError = useCallback(() => setError(""), []);

  const hydrateAuthStudent = useCallback((nextStudent: Student | null) => {
    setStudent((previousStudent) =>
      areStudentsEqual(previousStudent, nextStudent)
        ? previousStudent
        : nextStudent,
    );

    const nextStatus =
      nextStudent?.attendance_status_id && nextStudent.attendance_status
        ? {
            attendance_status_id: nextStudent.attendance_status_id,
            attendance_status: nextStudent.attendance_status,
          }
        : null;

    setStudentStatus((previousStatus) =>
      areStudentStatusesEqual(previousStatus, nextStatus)
        ? previousStatus
        : nextStatus,
    );

    if (nextStudent) {
      localStorage.setItem(STUDENT_STORAGE_KEY, JSON.stringify(nextStudent));
      setStatus("success");
      return;
    }

    localStorage.removeItem(STUDENT_STORAGE_KEY);
  }, []);

  const clearAuthState = useCallback(() => {
    hydrateAuthStudent(null);
    localStorage.removeItem(STUDENT_STORAGE_KEY);
    localStorage.removeItem("student.auth.token");
    localStorage.removeItem("student.auth.mode");
    clearDashboardSnapshots();
    try {
      sessionStorage.clear();
    } catch {
      // ignore
    }
    setStatus("autherror");
  }, [hydrateAuthStudent]);

  const validateSession = useCallback(async () => {
    try {
      const response = await api.get(endpoints.auth.validate);

      const data = response.data as ValidateResponse;
      if (!data?.ok) {
        clearAuthState();
        return false;
      }
      setError("");
      setStatus("success");
      return true;
    } catch (requestError) {
      const statusCode = getApiStatusCode(requestError);
      const sessionIsInvalid =
        statusCode === 400 || statusCode === 401 || statusCode === 404;

      if (sessionIsInvalid) {
        clearAuthState();
        return false;
      }

      setStatus(statusCode ? "error" : "networkerror");
      setError(getApiErrorMessage(requestError, "Session check failed"));
      return null;
    }
  }, [clearAuthState]);

  const hydrateStudent = useCallback(async () => {
    const response = await api.get(endpoints.user.me);
    const data = response.data as MeResponse;
    if (!data?.ok || !data.student) {
      throw new Error("Authenticated student payload is missing");
    }
    hydrateAuthStudent(data.student);
  }, [hydrateAuthStudent]);

  const bootstrapFromDashboard = useCallback(async () => {
    const data = (await fetchSharedDashboard(async () => {
      const response = await api.get(
        endpoints.dashboard.current,
        getDashboardRequestParams(),
      );
      return response.data as DashboardBootstrapResponse;
    })) as DashboardBootstrapResponse;

    if (!data?.me?.student) {
      throw new Error("Authenticated dashboard payload is missing");
    }

    hydrateAuthStudent(data.me.student);
    writeDashboardSnapshot(data.me.student.id, data);
  }, [hydrateAuthStudent]);

  const signIn = useCallback(
    async (personId: number, password: string) => {
      clearError();
      setStatus("loading");
      const response = await api.post(endpoints.auth.signIn, {
        person_id: personId,
        password,
      });

      const data = response.data as SignInResponse;
      if (!data?.ok || !data.student) {
        setStatus("autherror");
        throw new Error(data?.message || "Unable to sign in");
      }

      hydrateAuthStudent(data.student);
      setStatus("success");
    },
    [clearError, hydrateAuthStudent],
  );

  const signOut = useCallback(async () => {
    try {
      await api.post(endpoints.auth.revoke, {});
    } catch (e) {
      console.warn("Sign out request error:", e);
    } finally {
      clearAuthState();
    }
  }, [clearAuthState]);

  const refreshUser = useCallback(async () => {
    setStatus("loading");
    const valid = await validateSession();
    if (valid === true) {
      await hydrateStudent();
    }
  }, [validateSession, hydrateStudent]);

  useEffect(() => {
    const handleUnauthorizedSession = () => {
      clearAuthState();
      setIsLoading(false);
    };

    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorizedSession);
    return () => {
      window.removeEventListener(
        AUTH_UNAUTHORIZED_EVENT,
        handleUnauthorizedSession,
      );
    };
  }, [clearAuthState]);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      try {
        const pathname = window.location.pathname;
        const isPublicRoute = pathname === "/login" || pathname === "/";
        const currentStoredStudent = getStoredStudent();

        if (currentStoredStudent) {
          setStatus("loading");
          const valid = await validateSession();
          if (cancelled) {
            return;
          }
          if (valid !== true) {
            return;
          }

          await hydrateStudent();
          if (cancelled) {
            return;
          }
          return;
        }

        if (!isPublicRoute) {
          await bootstrapFromDashboard();
          if (cancelled) {
            return;
          }
          return;
        }

        setStatus("loading");
        const valid = await validateSession();
        if (cancelled) {
          return;
        }
        if (valid !== true) {
          return;
        }

        await hydrateStudent();
        if (cancelled) {
          return;
        }
      } catch (e: unknown) {
        if (!cancelled) {
          if (getApiStatusCode(e) === 401) {
            clearAuthState();
          } else {
            setStatus(getApiStatusCode(e) ? "error" : "networkerror");
          }
          setError(getApiErrorMessage(e, "Session check failed"));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void bootstrap();

    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      student,
      user: student,
      studentStatus,
      token,
      authMode,
      isAuthenticated: Boolean(student),
      isLoading,
      status,
      error,
      signIn: async (...args) => {
        try {
          await signIn(...args);
        } catch (e: unknown) {
          setStatus(getApiStatusCode(e) ? "error" : "networkerror");
          setError(getApiErrorMessage(e, "Sign in failed"));
          throw e;
        }
      },
      hydrateAuthStudent,
      refreshUser,
      validateSession,
      signOut,
      clearError,
    }),
    [
      student,
      studentStatus,
      isLoading,
      status,
      error,
      signIn,
      hydrateAuthStudent,
      refreshUser,
      validateSession,
      signOut,
      clearError,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
